import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, X, Receipt, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Phase 22b — TransportAccountsAckModal (stage: `transport_accounts_ack`).
 *
 * Dual-approver: either Sangita.K OR Amey.G can act. Fields captured:
 *   • accounts_ack_remark (required)
 *   • accounts_ack_confirmed (checkbox, must be true)
 */
export default function TransportAccountsAckModal({ open, onClose, onDone, requisition, actingAsBanner = null }) {
  const [remark, setRemark] = useState("");
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const taRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setRemark(""); setOk(false); setError("");
    setTimeout(() => taRef.current?.focus(), 20);
  }, [open, requisition?.ref_code]);

  if (!open || !requisition) return null;
  const trimmed = remark.trim();
  const canSubmit = !busy && trimmed.length > 0 && ok;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post(
        `/requisitions/${requisition.ref_code}/action`,
        {
          action: "transport_accounts_ack",
          remarks: trimmed,
          accounts_ack_remark: trimmed,
          accounts_ack_confirmed: ok,
        },
      );
      toast.success(`Accounts acknowledged — ${requisition.ref_code}`);
      onDone?.(data);
      onClose?.();
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg, { duration: 5000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="transport-accounts-ack-modal"
    >
      <div
        className="w-full max-w-lg bg-[#fffdf9] rounded-xl shadow-2xl border border-[#e2d7c3] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2d7c3] bg-white">
          <div className="flex items-center gap-2">
            <Receipt className="text-[#7a1f2b]" size={18} />
            <h3 className="font-serif text-lg font-semibold text-[#1f2320]">
              Accounts Ack — {requisition.ref_code}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#f4e9eb]" aria-label="Close">
            <X size={18} className="text-[#4a524c]" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {actingAsBanner && (
            <div className="text-xs text-[#7a1f2b] bg-[#faf5eb] border border-[#b48a45] rounded px-3 py-2 flex items-start gap-2"
                 data-testid="super-admin-acting-as-banner">
              <ShieldAlert size={14} className="mt-0.5 shrink-0 text-[#b48a45]" />
              <div>You are acting as <strong>{actingAsBanner}</strong>.</div>
            </div>
          )}
          <div className="text-xs text-[#4a524c] bg-[#faf5eb] border-l-4 border-[#b48a45] px-3 py-2 rounded">
            Either <strong>Sangita.K</strong> or <strong>Amey.G</strong> may acknowledge
            on behalf of Accounts. Only one of you needs to act.
          </div>
          {error && (
            <div className="text-xs text-white bg-[#b3261e] px-3 py-2 rounded" data-testid="modal-server-error">
              {error}
            </div>
          )}
          <label className="block">
            <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
              Remark <span className="text-[#b3261e]">*</span>
            </span>
            <textarea
              ref={taRef}
              value={remark}
              onChange={(e) => setRemark(e.target.value.slice(0, 1000))}
              rows={4}
              className="input-vgws min-h-[100px]"
              placeholder="Budget available, GL confirmed, imprest check done…"
              data-testid="accounts-ack-remark"
            />
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={ok}
              onChange={(e) => setOk(e.target.checked)}
              className="mt-1"
              data-testid="accounts-ack-confirmed"
            />
            <span>I confirm the accounts review is complete and the requisition can advance to Admin-1.</span>
          </label>
        </div>
        <div className="px-5 py-4 border-t border-[#e2d7c3] flex items-center justify-end gap-2 bg-[#faf5eb]">
          <button onClick={onClose} className="btn-ghost" data-testid="accounts-ack-cancel">Cancel</button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white border shadow-sm bg-[#2d6b6b] hover:bg-[#1c4a4a] border-[#1c4a4a] ${!canSubmit ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid="accounts-ack-confirm"
          >
            {busy ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <CheckCircle2 size={16} />}
            {busy ? "Submitting…" : "Acknowledged"}
          </button>
        </div>
      </div>
    </div>
  );
}
